import {
  CircularProgress,
  Paper,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  getFormEditorControlData,
  getFormEditorIsAutoSave,
  getSelectedFormEditorForm,
  setIsAutoSaveFormEditor,
  unSelectInputField,
} from '../store/slices/formEditorSlice';
import {
  getEmployerBusinessesThunk,
  updateFormEditorFormThunk,
} from '../store/thunks/formEditorThunks';
import { InputFieldsNew } from './InputFieldsNew/InputFieldsNew';
import { LeftColumnWidgets } from './LeftColumnWidgets/LeftColumnWidgets';
import RightColumnFieldEditor from './RightColumnFieldEditor/RightColumnFieldEditor';

export default function FormEditorWrapper() {
  const theme = useTheme();
  const dispatch = useDispatch();
  const selectedForm = useSelector(getSelectedFormEditorForm);
  const isAutoSave = useSelector(getFormEditorIsAutoSave);
  const controlData = useSelector(getFormEditorControlData);
  const [selectedPageIndex, setSelectedPageIndex] = useState(0);
  const [isLandscape, setIsLandscape] = useState(false);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

  const { enqueueSnackbar } = useSnackbar();

  const toggleAutoSave = () => {
    dispatch(setIsAutoSaveFormEditor(!isAutoSave));
  };

  useEffect(() => {
    if (selectedForm) {
      console.log('media url asetettu!');
    }
  }, [selectedForm, isAutoSave, controlData]);

  useEffect(() => {
    dispatch(getEmployerBusinessesThunk());
  }, [dispatch]);

  const onUpdateFormEditorThunk = () => {
    enqueueSnackbar('Tallennettu onnistuneesti', { variant: 'success' });
    dispatch(updateFormEditorFormThunk());
  };

  const styles = useMemo(() => {
    const card = {
      backgroundColor: '#005a7f',
      border: '1px solid rgba(255, 255, 255, 0.08)',
      padding: 14,
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    };
    const label = {
      fontSize: 11,
      letterSpacing: 0.4,
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.6)',
      marginBottom: 6,
    };
    const input = {
      width: '100%',
      padding: '10px 12px',
      borderRadius: 10,
      border: '1px solid rgba(255,255,255,0.15)',
      background: 'rgba(255,255,255,0.06)',
      color: '#fff',
      outline: 'none',
    };
    const btn = {
      border: 'none',
      borderRadius: 12,
      padding: '9px 12px',
      color: '#0f1220',
      fontWeight: 600,
      cursor: 'pointer',
      background: 'linear-gradient(180deg, #79ffe1 0%, #4ad3ff 100%)',
      boxShadow: '0 6px 14px rgba(74, 211, 255, 0.25)',
    };
    const textBtn = {
      ...btn,
      background: 'transparent',
      color: '#d8e8ff',
      border: '1px solid rgba(216,232,255,0.25)',
      boxShadow: 'none',
    };

    return {
      page: {
        minHeight: '100vh',
        color: theme.palette.text.primary,
        padding: 16,
        fontFamily: theme.typography.fontFamily,
      },
      header: {
        ...card,
        height: 48,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 16,
      },
      title: { fontSize: 18, fontWeight: 800 },
      layout: {
        display: 'grid',
        gridTemplateColumns: '280px 1fr 360px',
        gap: 12,
        height: 'calc(100vh - 48px - 16px*2 - 10px)', // viewport minus header & padding
      },
      column: {
        display: 'grid',
        gridAutoRows: 'min-content',
        alignContent: 'start',
        gap: 10,
        // overflow: 'auto',
        paddingRight: 4,
      },
      card,
      label,
      input,
      btn,
      textBtn,

      toggleWrap: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      toggle: {
        position: 'relative',
        width: 48,
        height: 24,
        background: isAutoSave ? '#4ad3ff' : 'rgba(255,255,255,0.18)',
        borderRadius: 12,
        transition: 'all .15s ease',
        cursor: 'pointer',
      },
      knob: {
        position: 'absolute',
        top: 1,
        left: isAutoSave ? 27 : 3,
        width: 19,
        height: 19,
        borderRadius: '50%',
        background: '#0f1220',
        boxShadow: '0 2px 6px rgba(0,0,0,0.35)',
        transition: 'left .15s ease',
      },

      // center preview
      previewCard: { ...card, display: 'grid', gap: 10 },

      previewCanvasWrap: {
        display: 'flex',
        justifyContent: 'center',
        overflow: 'auto',
        minHeight: isLandscape ? '900px' : '600px',
      },
      paperFrame: {
        width: isLandscape ? 842 : 595,
        height: isLandscape ? 595 : 842,
        position: 'relative',
        transform: isLandscape
          ? isSmallScreen
            ? 'scale(0.9)'
            : 'scale(1.4)'
          : 'scale(1.1)',
        transformOrigin: 'center top',
      },
      a4: {
        width: '100%', // fill the frame
        height: '100%', // fill the frame
        background:
          'linear-gradient(135deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))',
        display: 'grid',
        placeItems: 'center',
        position: 'relative',
      },
      overlay: {
        position: 'absolute',
        inset: 0,
        background: `rgba(74, 211, 255, 0.7)`,
        pointerEvents: 'none',
      },
      a4Outer: {
        position: 'relative',
        width: isLandscape ? 842 : 595,
        height: isLandscape ? 595 : 842,
        transformOrigin: 'top left',
      },

      previewHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      small: { fontSize: 12, color: 'rgba(255,255,255,0.6)' },

      // left specifics
      upload: {
        ...card,
        display: 'grid',
        gap: 8,
        textAlign: 'center',
        borderStyle: 'dashed',
        borderColor: 'rgba(255,255,255,0.12)',
        padding: 18,
      },
      chips: { display: 'flex', gap: 6, flexWrap: 'wrap' },
      chip: {
        padding: '6px 10px',
        borderRadius: 999,
        background: 'rgba(255,255,255,0.1)',
        border: '1px solid rgba(255,255,255,0.12)',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 12,
      },
      chipX: {
        cursor: 'pointer',
        background: 'rgba(255,255,255,0.18)',
        borderRadius: 6,
        padding: '2px 6px',
      },

      // right: window list header
      propHeader: {
        ...card,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      },
      windowList: { display: 'flex', gap: 8, flexWrap: 'wrap' },
      windowPill: (active) => ({
        padding: '6px 10px',
        borderRadius: 999,
        border: '1px solid rgba(255,255,255,0.18)',
        background: active ? 'rgba(74,211,255,0.2)' : 'transparent',
        color: active ? '#c9f5ff' : '#d8e8ff',
        cursor: 'pointer',
        fontSize: 12,
      }),
    };
  }, [isAutoSave, isLandscape, isSmallScreen]);

  return (
    <div style={styles.page}>
      <Paper style={styles.header}>
        <Typography variant="h5" style={{ ...styles.title, color: '#fff' }}>
          PDF Lomake-editori
        </Typography>

        <div
          style={{
            display: 'flex',
            gap: theme.spacing(2),
            alignItems: 'center',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={isAutoSave}
                onChange={toggleAutoSave}
                color="primary"
              />
            }
            label="Auto-tallennus"
            style={{ color: '#fff' }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={onUpdateFormEditorThunk}
          >
            Tallenna
          </Button>
        </div>
      </Paper>

      {/* 3-COLUMN LAYOUT */}
      <div style={styles.layout}>
        {/* LEFT COLUMN */}
        <LeftColumnWidgets
          selectedPageIndex={selectedPageIndex}
        ></LeftColumnWidgets>

        {/* CENTER COLUMN */}
        <div
          style={{
            ...styles.column,
            alignContent: 'start',
            gap: 10,
          }}
        >
          <div style={styles.previewCard}>
            <div style={styles.previewHeader}>
              <div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>
                  PDF Esikatselu
                </div>
                <div
                  style={{ ...styles.small, color: 'rgba(255,255,255,0.6)' }}
                >
                  Nimi: <b>{selectedForm?.name}</b>
                </div>
              </div>
              {selectedForm?.mediaUrls?.length > 1 && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <button
                    onClick={() => {
                      setSelectedPageIndex(Math.max(0, selectedPageIndex - 1));
                      dispatch(unSelectInputField());
                    }}
                    disabled={selectedPageIndex === 0}
                    style={styles.textBtn}
                  >
                    ←
                  </button>
                  <span style={{ color: '#fff', fontSize: 12 }}>
                    {selectedPageIndex + 1} / {selectedForm.mediaUrls.length}
                  </span>
                  <button
                    onClick={() => {
                      setSelectedPageIndex(
                        Math.min(
                          selectedForm.mediaUrls.length - 1,
                          selectedPageIndex + 1
                        )
                      );
                      dispatch(unSelectInputField());
                    }}
                    disabled={
                      selectedPageIndex === selectedForm.mediaUrls.length - 1
                    }
                    style={styles.textBtn}
                  >
                    →
                  </button>
                </div>
              )}
            </div>

            <div style={styles.previewCanvasWrap}>
              <div style={styles.paperFrame}>
                {selectedForm ? (
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                    }}
                  >
                    <div style={styles.a4Outer}>
                      <img
                        src={
                          selectedForm?.mediaUrls?.[selectedPageIndex] ||
                          selectedForm?.mediaUrl
                        }
                        alt="media"
                        onLoad={(e) => {
                          const { naturalWidth, naturalHeight } = e.target;
                          setIsLandscape(naturalWidth > naturalHeight);
                        }}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                        }}
                      />
                      <InputFieldsNew
                        selectedPageIndex={selectedPageIndex}
                        isLandscape={isLandscape}
                        containerWidth={isLandscape ? 842 : 595}
                        containerHeight={isLandscape ? 595 : 842}
                      />
                    </div>
                  </div>
                ) : (
                  <div style={styles.a4}>
                    {controlData.isLoading ? (
                      <CircularProgress color="inherit" size={60} />
                    ) : (
                      <span style={{ color: 'white', zIndex: '9' }}>
                        A4 PDF tulee tähän
                      </span>
                    )}

                    <div style={styles.overlay} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div style={styles.column}>
          {controlData.selectedInputFieldIds?.length > 1 ? (
            <RightColumnFieldEditor
              selectedInputField={null}
              selectedInputFieldIds={controlData.selectedInputFieldIds}
              selectedPageIndex={selectedPageIndex}
            ></RightColumnFieldEditor>
          ) : controlData.selectedInputField ? (
            <RightColumnFieldEditor
              selectedInputField={controlData.selectedInputField}
              selectedInputFieldIds={controlData.selectedInputFieldIds}
              selectedPageIndex={selectedPageIndex}
            ></RightColumnFieldEditor>
          ) : (
            <div style={{ ...styles.card, color: 'rgba(255,255,255,0.7)' }}>
              Syöte ikkunaa ei ole valittu, valitse tai luo uusi.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
